import classNames from 'classnames'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import type ReCAPTCHADefinition from 'react-google-recaptcha'
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import siteConfig from '../../gatsby/siteConfig'
import { usePageContext } from '../../hooks/usePageContext'
import { useTimer } from '../../hooks/useTimer'
import { useTranslations } from '../../hooks/useTranslations'
import { getCookieValue } from '../../utilities/cookie'
import { isBrowser } from '../../utilities/isBrowser'
import { sendToFivetran } from '../../utilities/sendToFivetran'
import { IS_EQT_NEXUS } from '../../utilities/site'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { ArrowIcon } from '../ArrowIcon'
import { Button } from '../Button'
import { Paragraph } from '../Paragraph'
import { Inputs } from './Inputs/Inputs'
import { RHFRecaptcha } from './Inputs/RHFReCaptcha'
import { FormInputProps } from './types'

const cloudflareFallbackURLs = [
  'https://one.one.one.one/cdn-cgi/trace',
  'https://1.0.0.1/cdn-cgi/trace',
  'https://cloudflare-dns.com/cdn-cgi/trace',
  'https://cloudflare-eth.com/cdn-cgi/trace',
  'https://cloudflare-ipfs.com/cdn-cgi/trace',
  'https://workers.dev/cdn-cgi/trace',
  'https://pages.dev/cdn-cgi/trace',
  'https://cloudflare.tv/cdn-cgi/trace',
]

const getCloudflareJSON = async () => {
  const dataString = await fetchWithFallback(cloudflareFallbackURLs)
  if (dataString) {
    const parsedData = dataString
      .trim()
      .split('\n')
      .map(e => e.split('='))
    return Object.fromEntries(parsedData)
  }

  return {}
}

const fetchWithFallback = async (links: string[]) => {
  for (const link of links) {
    try {
      const response = await fetch(link)
      if (response.ok) {
        return response.text()
      }
    } catch (e) {}
  }
  return ''
}

export type FormProps = {
  inputs?: Partial<FormInputProps>[]
  id?: string
  name?: string
  minimumSubmissionTimeMs?: number
  onSubmitted?: (success: boolean) => void
  onSubmitting?: () => void
  slug: string
  submitText: string
  downloadFileOnSubmit?: boolean
}

const encode = (data: object) => {
  const formData = new FormData()

  for (const key of Object.keys(data)) {
    formData.append(key, data[key])
  }

  return formData
}

export const Form = ({
  inputs,
  id,
  name,
  minimumSubmissionTimeMs,
  onSubmitted,
  onSubmitting,
  slug,
  submitText,
  downloadFileOnSubmit = false,
}: FormProps) => {
  const providerProps = useForm({
    mode: 'onTouched',
  })

  const { path } = usePageContext()

  const timer = useTimer()

  const translations = useTranslations()

  const reCaptchaRef = useRef<ReCAPTCHADefinition>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const resultRef = useRef<boolean | null>(null)

  const { handleSubmit, register } = providerProps

  const filteredInputs = useMemo(
    () =>
      inputs?.filter(
        (input): input is FormInputProps => !isNullOrWhiteSpace(input?.slug)
      ) ?? [],
    [inputs]
  )

  const currentUrl = process.env.GATSBY_BASE_URL ?? ''
  let site = currentUrl

  if (currentUrl.includes('foundation')) {
    site = 'foundation'
  } else if (currentUrl.includes('nexus')) {
    site = currentUrl.includes('nexus-au') ? 'nexus_au' : 'nexus'
  }

  const onSubmit = useCallback<SubmitHandler<FieldValues>>(
    async data => {
      if (
        isSubmitting === false &&
        process.env.GATSBY_FORM_ENDPOINT &&
        reCaptchaRef.current
      ) {
        setIsSubmitting(true)
        onSubmitting?.()

        let success = false

        try {
          if (minimumSubmissionTimeMs) {
            // Start a timer to allow showing a loading state for a minimum amount of time
            timer.start(() => {
              if (resultRef.current !== null) {
                setIsSubmitting(false)
                onSubmitted?.(resultRef.current)
              }

              resultRef.current = null
            }, minimumSubmissionTimeMs)
          }

          const reCaptchaToken = await reCaptchaRef.current.executeAsync()

          if (reCaptchaToken) {
            const result = await fetch(process.env.GATSBY_FORM_ENDPOINT, {
              body: reCaptchaToken
                ? encode({ ...data, 'g-recaptcha-response': reCaptchaToken })
                : encode(data),
              method: 'POST',
            })

            success = result.ok

            if (!result.ok) {
              console.error(`API error code: ${result.status}`)
            }
          } else {
            console.error('Client ReCaptcha error')
          }

          // Resetting the reCaptcha widget seems to be required to be able to make additional submissions
          reCaptchaRef.current.reset()
        } catch (e) {
          console.error(e)

          success = false
        }

        const cloudflareResponse = await getCloudflareJSON()

        const fivetranData = IS_EQT_NEXUS
          ? {
              website: site,
              country: cloudflareResponse?.loc ?? 'N/A',
              investor: isBrowser() ? getCookieValue('eqt_tsd_investor_type') : '',
              date: new Date(),
              formId: id,
              formName: name,
              url: currentUrl,
            }
          : null

        if (timer.isRunning()) {
          sendToFivetran(fivetranData)
          resultRef.current = success
        } else {
          sendToFivetran(fivetranData)
          setIsSubmitting(false)
          onSubmitted?.(success)
        }
      }
    },
    [
      currentUrl,
      id,
      isSubmitting,
      minimumSubmissionTimeMs,
      name,
      onSubmitted,
      onSubmitting,
      site,
      timer,
    ]
  )

  return (
    <FormProvider {...providerProps}>
      <form
        action={process.env.GATSBY_FORM_ENDPOINT}
        id={id}
        method="POST"
        name={slug}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        {!isNullOrWhiteSpace(siteConfig.FormsReCaptchaKey) && (
          <RHFRecaptcha ref={reCaptchaRef} siteKey={siteConfig.FormsReCaptchaKey} />
        )}

        <input {...register('formSlug')} type="hidden" value={slug} />

        <input
          {...register('formUrl')}
          type="hidden"
          value={`${process.env.GATSBY_BASE_URL}${path}`}
        />

        {!isNullOrWhiteSpace(site) && (
          <input {...register('siteName')} type="hidden" value={site} />
        )}

        <Inputs formId={id} inputs={filteredInputs} />

        <div className="mt-10 first:mt-0">
          <Button
            type="submit"
            additionalClassName={`${
              !!downloadFileOnSubmit ? 'flex-row flex gap-5 items-center' : ''
            }`}
          >
            {submitText}
            {!!downloadFileOnSubmit && <ArrowIcon additionalClassName="w-9 h-9" />}
          </Button>
        </div>

        {!isNullOrWhiteSpace(translations.reCaptchaDisclaimer) && (
          <div className="mt-30">
            <Paragraph
              className={classNames(
                'font-t-light italic text-t-200 text-shade-medium-dim',
                '[&>a]:font-t-bold [&>a]:border-b [&>a]:border-quinary [&>a]:transition-hover hover:[&>a]:opacity-hover hover:[&>a]:border-opacity-50 hover:[&>a]:border-secondary-darker'
              )}
            >
              {translations.reCaptchaDisclaimer}
            </Paragraph>
          </div>
        )}
      </form>
    </FormProvider>
  )
}
